import { loadConnectAndInitialize } from '@stripe/connect-js';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { api } from '../../lib/api';
import { useCan, useFeature } from '../../stores/session';

/**
 * The Stripe Connect instance, in one place.
 *
 * ## Why this is not inside a panel any more
 *
 * `loadConnectAndInitialize` mounts an iframe, and calling it twice gives two
 * that race over the same account. That was fine while every embedded component
 * lived on one page; it stops being fine the moment payouts and balances have
 * pages of their own. The instance is built once by the layout and handed down
 * through `ConnectComponentsProvider`, so a page only ever renders a component.
 */

export interface Status {
  connected: boolean;
  payouts_enabled: boolean;
  details_submitted: boolean;
  requirements: string[];
}

/**
 * A design token, resolved to the literal value behind it.
 *
 * For the one consumer that cannot take a `var()`: Stripe's embedded components
 * live in an iframe of their own, where our custom properties do not exist. The
 * fallback is what the panel used before these were read from the stylesheet,
 * and covers a context with no computed style at all.
 */
function token(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;

  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}

/**
 * What Stripe last told us about this account.
 *
 * One query key across every caller, so a page showing several of these asks
 * once. Disabled for anybody it cannot apply to rather than fetching a 403.
 */
export function useStripeStatus() {
  const enabled = useFeature('payments');
  const isHost = useCan('host.broadcast');

  const query = useQuery({
    queryKey: ['stripe', 'status'],
    queryFn: () => api.get<Status>('/me/stripe'),
    enabled: enabled && isHost,
  });

  return { ...query, enabled, isHost, applies: enabled && isHost };
}

/**
 * Whether this account can be paid yet.
 *
 * `undefined` while it is unknown - payments off, not a host, or the request
 * still in flight. Callers use it to *add* a warning, never to hide something,
 * so an unknown answer stays quiet rather than guessing in either direction.
 */
export function usePayoutsReady(): boolean | undefined {
  const { data, applies } = useStripeStatus();

  if (!applies || !data) {
    return undefined;
  }

  return data.payouts_enabled;
}

/**
 * Built once there is an account to point it at, and not before.
 *
 * `fetchClientSecret` is called by Stripe's script when it needs one, including
 * when the old one expires mid-session. Holding one in state would work until
 * somebody left the tab open over lunch.
 */
export function useConnectInstance(connected: boolean | undefined) {
  return useMemo(() => {
    const key = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

    if (!key || !connected) return null;

    return loadConnectAndInitialize({
      publishableKey: key,
      fetchClientSecret: async () => {
        const session = await api.post<{ client_secret: string }>('/me/stripe/account');

        return session.client_secret;
      },
      /*
       * Stripe's components render in an iframe we cannot style, so the only
       * lever is this palette. A bright white form in the middle of a dark page
       * reads as a broken embed rather than as part of the product.
       *
       * Read from the stylesheet rather than written out again. Stripe needs
       * literal colours - an iframe cannot resolve our custom properties - but
       * that is a reason to *resolve* them here, not a reason to keep a second
       * copy of the palette that nothing would update when the first one
       * changed. It also means the embed follows a theme switch for free.
       */
      appearance: {
        overlays: 'dialog',
        variables: {
          colorPrimary: token('--color-accent', '#7c7cf0'),
          colorBackground: token('--color-raised', '#12122a'),
          colorText: token('--color-ink', '#e8e8f5'),
          colorSecondaryText: token('--color-soft', '#9a9ab8'),
          colorBorder: token('--color-frame', '#26264d'),
          borderRadius: '10px',
        },
      },
    });
  }, [connected]);
}
